'server only'

import type { AsyncLocalStorage } from 'node:async_hooks'

import { log } from '@src/utils/log.util'

type UnauthorizedState = { detected: boolean }

// React cache only memoizes during a render, not inside Server Actions.
// Importing node:async_hooks breaks the build; a client bundle reaches this module.
const GlobalAsyncLocalStorage = (
  globalThis as { AsyncLocalStorage?: typeof AsyncLocalStorage<UnauthorizedState> }
).AsyncLocalStorage
const unauthorizedScope = GlobalAsyncLocalStorage && new GlobalAsyncLocalStorage()

// Only a 401 on a request that carried Authorization is fixable by logging in.
export function reportUnauthorized(response: Pick<Response, 'status'>, headers?: HeadersInit) {
  if (response.status !== 401 || !new Headers(headers).has('Authorization')) return
  const state = unauthorizedScope?.getStore()
  if (state) state.detected = true
}

// Nested calls join the outer scope, so the redirect fires once after all service catches run.
export async function redirectOnUnauthorized<T>(name: string, fn: () => Promise<T>): Promise<T> {
  if (!unauthorizedScope || unauthorizedScope.getStore()) return fn()

  const state: UnauthorizedState = { detected: false }
  try {
    return await unauthorizedScope.run(state, fn)
  } finally {
    if (state.detected) await redirectToLogin(name)
  }
}

// Only /app redirects. Lazy imports keep these modules out of every service module.
async function redirectToLogin(name: string) {
  const { configPublic } = await import('@src/config.public')
  if (!configPublic.featureFlags.unauthorizedRedirectsEnabled) return

  let loginUrl: string
  try {
    const [{ isAppPath }, { getCurrentServerURL }, { AuthUtils }] = await Promise.all([
      import('@src/i18n/locale.util'),
      import('@src/utils/url.util'),
      import('./auth.util'),
    ])
    if (!isAppPath((await getCurrentServerURL()).pathname)) return
    loginUrl = await AuthUtils.getLoginUrl()
  } catch (error: any) {
    log.warn('[unauthorized] Could not resolve the request context; ignoring the 401.', error)
    return
  }

  const { redirect, RedirectType } = await import('next/navigation')
  log.info(`[unauthorized] ${name} - session rejected upstream; redirecting to login.`)
  redirect(loginUrl, RedirectType.replace)
}
